import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ApiEndpoints } from "../../helper/ApiEndpoints";
import ApiRequest from "../../helper/ApiRequest";
import CustomSelect from "../../component/CustomSelect";
import CustomImageUpload from "../../component/CustomImageUpload";
import { toast } from "react-toastify";
import AdminHeader from "../../component/AdminHeader";
import DynamicCKEditor from "../../component/DynamicCkEditor";

const createEndpoint = ApiEndpoints.newsletter.Create;
const filterfetchEndpoint = ApiEndpoints.filters.List;
const tagListEndpoint = ApiEndpoints.tags.tagList;
const authorListEndpoint = ApiEndpoints.autor.authorList;

export default function AddNewsletterPage() {
  const navigate = useNavigate();

  const [authors, setAuthors] = useState([]);
  const [filters, setFilters] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [tags, setTags] = useState([]);

  const [previews, setPreviews] = useState({ banner_image: null, thumbnail: null });
  const [postData, setPostData] = useState({
    specialty_id: "", provider_id: "", solution_id: "", title: "", slug: "", banner_image: null, alt_banner_image: "",form_heading: "", form_subtext: "", form_cta_name: "",  thumbnail: null, alt_thumbnail: "", tags: "", description: "", brief:"", reference_data: "", author_id: "", date: "", dynamic_form_title: "", dynamic_form_description: "", faqs: [{ question: "", answer: "" }, { question: "", answer: "" },],
  });

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const response = await ApiRequest("GET", filterfetchEndpoint);
        setFilters(response || {});
      } catch (error) {
        console.error("Failed to fetch categories:", error);
        setFilters({});
      }
    };

    const fetchTags = async () => {
      try {
        const response = await ApiRequest("GET", tagListEndpoint);
        setTags(response?.tags || []);
      } catch (error) {
        console.error("Failed to fetch tags:", error);
        setTags([]);
      }
    };

    const fetchAuthors = async () => {
      try {
        const response = await ApiRequest("GET", authorListEndpoint);
        setAuthors(response?.authors || []);
      } catch (error) {
        console.error("Failed to fetch authors:", error);
        setAuthors([]);
      }
    };

    fetchFilters();
    fetchTags();
    fetchAuthors();
  }, []);

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (files) {
      const file = files[0];
      // Update the previews state
      setPreviews((prev) => ({
        ...prev,
        [name]: URL.createObjectURL(file),
      }));

      // Update the postData state
      setPostData((prev) => ({
        ...prev,
        [name]: file,
      }));
    } else {
      // For non-file inputs, update the postData state directly
      setPostData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleEditorChange = (key, data) => {
    setPostData((prevState) => ({
      ...prevState,
      [key]: data,
    }));
  };

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    for (const [key, value] of Object.entries(postData)) {
      if (key !== "solution_id" && key !== "reference_data" && key !== "provider_id" && key !== "specialty_id" && (value === "" || value === null)) {
        toast.error(`The field "${key}" is mandatory.`);
        return;
      }
    }

    const formData = new FormData();
    Object.entries(postData).forEach(([key, value]) => {
      if (key === "faqs") {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, value);
      }
    });

    try {
      const response = await ApiRequest("POST", createEndpoint, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success(response.message);
      navigate("/newsletter-page");
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };


  // ======================Tags Drag and Drop Functionality========================

  // Accepts array of IDs, array of objects, or comma string
  const handleTagSelectChange = (e) => {
    const { name, value } = e.target; // name should be "tag1"
    let newSelected = [];

    if (Array.isArray(value)) {
      if (value.length > 0 && typeof value[0] === "string") {
        // array of IDs -> map to tag objects from `tags` list
        newSelected = value
          .map((id) => tags.find((t) => t._id === id))
          .filter(Boolean);
      } else {
        // assume array of objects already
        newSelected = value;
      }
    } else if (typeof value === "string") {
      // maybe a comma-separated string "id1,id2"
      const ids = value ? value.split(",").map((s) => s.trim()).filter(Boolean) : [];
      newSelected = ids.map((id) => tags.find((t) => t._id === id)).filter(Boolean);
    } else {
      newSelected = [];
    }

    setSelectedTags(newSelected);

    // keep postData.tag1 as comma string for backend compatibility
    setPostData((prev) => ({
      ...prev,
      [name]: newSelected.map((t) => t._id).join(","),
    }));
  };

  // ------------------ Drag & Drop for tag chips ------------------
  const onDragStartTag = (e, index) => {
    e.dataTransfer.setData("text/plain", String(index));
    e.dataTransfer.effectAllowed = "move";
  };

  const onDragOverTag = (e) => {
    e.preventDefault(); // allow drop
    e.dataTransfer.dropEffect = "move";
  };

  const onDropTag = (e, dropIndex) => {
    e.preventDefault();
    const dragIndexStr = e.dataTransfer.getData("text/plain");
    if (!dragIndexStr) return;
    const dragIndex = parseInt(dragIndexStr, 10);
    if (isNaN(dragIndex) || dragIndex === dropIndex) return;

    setSelectedTags((prev) => {
      const next = Array.from(prev);
      const [moved] = next.splice(dragIndex, 1);
      next.splice(dropIndex, 0, moved);

      const ids = next.map((t) => t._id);
      setPostData((p) => ({
        ...p,
        tags: ids.join(","),
      }));

      return next;
    });
  };

  const removeTagAt = (index) => {
    setSelectedTags((prev) => {
      const next = prev.filter((_, i) => i !== index);
      const ids = next.map((t) => t._id);
      setPostData((p) => ({
        ...p,
        tags: ids.join(","),
      }));
      return next;
    });
  };

  const renderSelectedTagsChips = () => {
    if (!selectedTags || selectedTags.length === 0)
      return <div style={{ color: "#666", fontSize: 12, marginTop: 15 }}>SELECTED TAGS: (You can drag and drop them to change their order.)</div>;

    return (
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 15 }}>
        {selectedTags.map((t, i) => (
          <span key={t._id} draggable onDragStart={(e) => onDragStartTag(e, i)} onDragOver={onDragOverTag} onDrop={(e) => onDropTag(e, i)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 10px",
              borderRadius: 16,
              border: "1px solid #d0d0d0",
              background: "#fff",
              cursor: "grab",
              userSelect: "none",
            }}
            title={t.name || t.title}
          >
            <span style={{ fontSize: 13 }}>{t.name || t.title}</span>
            <button type="button" onClick={(ev) => { ev.stopPropagation(); removeTagAt(i); }}
              style={{
                border: "none",
                background: "transparent",
                padding: 0,
                marginLeft: 4,
                cursor: "pointer",
                lineHeight: 1,
                color: "red",
              }}
              aria-label={`Remove ${t.name || t.title}`}
            >
              ✕
            </button>
          </span>
        ))}
      </div>
    );
  };

  // =====================Tag Drag and Drop End===================================


  // =========FAQS Functionality==========

  // 🔹 handle change for question/answer
  const handleFaqChange = (index, field, value) => {
    setPostData((prev) => {
      const updatedFaqs = [...prev.faqs];
      updatedFaqs[index][field] = value;
      return { ...prev, faqs: updatedFaqs };
    });
  };

  // 🔹 add new FAQ
  const addFaq = () => {
    setPostData((prev) => ({
      ...prev,
      faqs: [...prev.faqs, { question: "", answer: "" }],
    }));
  };

  // 🔹 remove FAQ
  const removeFaq = (index) => {
    setPostData((prev) => ({
      ...prev,
      faqs: prev.faqs.filter((_, i) => i !== index),
    }));
  };

  // =======FAQs Functionality End==========


  const pagename = {
    name: 'newsletter Management',
    user: 'Admin',
    innerpage: 'newsletter Management / newsletter / Add newsletter'
  }

  return (
    <>
      <AdminHeader pagename={pagename} />
      <div className="add-content-page">
        <form id="videoAdd" onSubmit={handleSubmit}>

          <div className="add-content-wrap">
            <div className="row">

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>title<small>*</small></label>
                  </div>
                  <input onChange={handleChange} name="title" type="text" className="form-control" placeholder="Enter Title" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Slug<small>*</small></label>
                  </div>
                  <input onChange={handleChange} name="slug" type="text" className="form-control" placeholder="Enter Slug" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Date<small>*</small></label>
                  </div>
                  <input onChange={handleChange} name="date" type="date" className="form-control" placeholder="Enter Author Name" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Author<small>*</small></label>
                  </div>
                  <CustomSelect onChange={handleChange} name="author_id" options={authors} placeholder="Select Author Name" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Solution<small></small></label>
                  </div>
                  <CustomSelect multiple defaultValue={postData.solution_id} onChange={handleChange} name="solution_id" options={filters?.solutions} placeholder="Select solution" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Specialty<small></small></label>
                  </div>
                  <CustomSelect multiple defaultValue={postData.specialty_id} onChange={handleChange} name="specialty_id" options={filters?.specialities} placeholder="Select specialty" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Provider<small></small></label>
                  </div>
                  <CustomSelect multiple defaultValue={postData.provider_id} onChange={handleChange} name="provider_id" options={filters?.providers} placeholder="Select provider" />
                </div>
              </div>

              {/* Tags: MULTIPLE + DRAGGABLE CHIPS */}
              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>
                      Select Tags<small>*</small>
                    </label>
                  </div>

                  <CustomSelect multiple defaultValue={postData.tags} onChange={handleTagSelectChange} name="tags" options={tags} placeholder="Select Tag Name" />

                  {/* Selected tags shown as draggable spans */}
                  {renderSelectedTagsChips()}
                </div>
              </div>

            </div>
          </div>

          <div className="add-content-wrap">
            <div className="row">

              <div className="col-lg-12">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Short Description<small>*</small></label>
                  </div>
                  <input value={postData.brief} onChange={handleChange} name="brief" type="text" className="form-control" placeholder="Short Description" />
                </div>

                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Reference Data<small></small></label>
                  </div>
                  <DynamicCKEditor data={postData.reference_data} onChange={(event, editor) => handleEditorChange("reference_data", editor.getData())} />
                </div>
              </div>

            
            
              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Form   Title<small>*</small></label>
                  </div>
                  <input type="text" className="form-control" name="form_heading" onChange={handleChange} value={postData.form_heading} />
                </div>
              </div>
              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Form  Subtext<small>*</small></label>
                  </div>
                  <input type="text" className="form-control" name="form_subtext" onChange={handleChange} value={postData.form_subtext} />
                </div>
              </div>
              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Form  Button Text <small>*</small></label>
                  </div>
                  <input type="text" className="form-control" name="form_cta_name" onChange={handleChange} value={postData.form_cta_name} />
                </div>
              </div>
              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Form Left Title<small>*</small></label>
                  </div>
                  <input type="text" className="form-control" name="dynamic_form_title" onChange={handleChange} value={postData.dynamic_form_title} />
                </div>
              </div>
              <div className="col-lg-12">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Form Left Description<small>*</small></label>
                  </div>
                  <DynamicCKEditor data={postData.dynamic_form_description} onChange={(event, editor) => handleEditorChange("dynamic_form_description", editor.getData())} />
                </div>
              </div>

              <div className="col-lg-12">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Description<small>*</small></label>
                  </div>
                  <DynamicCKEditor data={postData.description} onChange={(event, editor) => handleEditorChange("description", editor.getData())} />
                </div>
              </div>

            </div>
          </div>

          <div className="add-content-wrap">
            <div className="row">

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Thumbnail Image Alt Tag<small>*</small></label>
                  </div>
                  <input value={postData.alt_thumbnail} onChange={handleChange} name="alt_thumbnail" type="text" className="form-control" placeholder="Alt Thumbnail" />
                </div>

                <div className="add-page-inputs input-add-image">
                  <div className="label-wrap-add">
                    <label>Thumbnail Image (508 × 670)<small>*</small></label>
                  </div>
                  <CustomImageUpload name="thumbnail"
                    // maxSize={550 * 1024}
                    acceptedFormats={[".webp"]}
                    onImageChange={handleChange}
                    imagePreview={previews.thumbnail} />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Banner Image Alt Tag<small>*</small></label>
                  </div>
                  <input value={postData.alt_banner_image} onChange={handleChange} name="alt_banner_image" type="text" className="form-control" placeholder="Alt Banner Image" />
                </div>

                <div className="add-page-inputs input-add-image">
                  <div className="label-wrap-add">
                    <label>Banner Image (1608 × 600)<small>*</small></label>
                  </div>
                  <CustomImageUpload name="banner_image"
                    // maxSize={550 * 1024}
                    acceptedFormats={[".webp"]}
                    onImageChange={handleChange}
                    imagePreview={previews.banner_image} />
                </div>
              </div>

            </div>
          </div>

          <div className="add-content-wrap">
            <h5>Faq Section</h5>
            <div className="row">

              {postData.faqs.map((faq, index) => (
                <div key={index} className="col-lg-6" style={{ position: "relative" }}>
                  <div className="add-page-inputs input-add-image">
                    <div className="label-wrap-add">
                      <label>Question<small>*</small></label>
                    </div>
                    <input required className="form-control" type="text" placeholder={`Question ${index + 1}`} value={faq.question} onChange={(e) => handleFaqChange(index, "question", e.target.value)} />
                  </div>

                  <div className="add-page-inputs input-add-image">
                    <div className="label-wrap-add">
                      <label>Answer<small>*</small></label>
                    </div>
                    <DynamicCKEditor data={faq.answer} onChange={(event, editor) => handleFaqChange(index, "answer", editor.getData())}/>
                  </div>
                  {index >= 2 && (
                    <button type="button" style={{ position: "absolute", background: "linear-gradient(270deg,#e20814,#a80008)", top: "5px", right: "10px", color: "#fff", border: "none", borderRadius: "8px", fontSize: "12px", padding: "3px 15px" }} onClick={() => removeFaq(index)}>
                      REMOVE
                    </button>
                  )}
                </div>
              ))}

              <div className="col-lg-12 text-center">
                <button type="button" className="add-faqs" onClick={addFaq} style={{ background: "green", top: "5px", right: "10px", color: "#fff", border: "none", borderRadius: "8px", fontSize: "14px", padding: "5px 15px", marginTop: "15px" }}>
                  Add New Faq
                </button>
              </div>
            </div>
          </div>

        </form>
        <button type="submit" form="videoAdd" className="default-btn">publish <span></span></button>
      </div>
    </>
  );
}
